/* @jsxImportSource @emotion/react */

import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ReactMarkdown from 'react-markdown'
import { useData } from 'vike-react/useData'

import { ImgSanity, SanityTextRenderer } from '@slite/components'
import { ChevronLeftIcon } from '@slite/icons'

import { Button } from '../../../components/Button/Button'
import { Section } from '../../../components/Section'
import { SectionCurve } from '../../../components/SectionCurve'
import { SlitePage } from '../../../components/SlitePage'
import { mediaQuerySmall } from '../../../components/style'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { getCategoryTitle, SECTION_WHAT_WE_OFFER } from '../data'
import { SectionWorkCulture } from '../SectionWorkCulture'
import type { Data } from './+data'

function JobDescriptionSection(props: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div
      className="padding-bottom-40 "
      css={{
        [mediaQuerySmall]: {
          paddingBottom: '32px',
        },
      }}
    >
      <h2 className="font-size-3 font-family-title font-weight-medium padding-bottom-16">
        {props.title}
      </h2>
      <div className="simple-rich-text font-size-5">{props.children}</div>
    </div>
  )
}

export default function JobShowPage() {
  const intl = useIntl()
  const routes = useRoutes()
  const { job } = useData<Data>()
  const categoryTitle = getCategoryTitle(intl, job.category)

  const introSection = job.sections?.find((section) =>
    section.title.toLowerCase().includes('intro')
  )
  const otherSections = job.sections?.filter(
    (section) => !section.title.toLowerCase().includes('intro')
  )

  const teamPeople = job.teamPeople

  return (
    <SlitePage
      title={`${job.title} | Jobs at Slite`}
      description={`Join Slite as ${job.title}. We're building the simplest possible tool making all knowledge accessible, by default, in teams.`}
      canonicalUrl={`/jobs/${job.slug.current}`}
      background="moon-dust"
    >
      <Section containerVerticalPadding={80} background="moon-dust">
        <div
          css={{
            maxWidth: '800px',
            margin: '0 auto',
          }}
        >
          {/* Back link */}
          <a
            href={`${routes.jobs()}#open-positions`}
            className="shade-05 font-size-7 flexbox font-weight-semibold"
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none',
              marginBottom: '56px',
              transition: 'opacity 0.2s',
              ':hover': {
                opacity: 0.6,
              },
            }}
          >
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <ChevronLeftIcon size={12} />
            <FormattedMessage
              id="jobs.show.backToJobs"
              defaultMessage="Back to all jobs"
            />
          </a>

          {/* Job Header */}
          <div className="padding-bottom-40">
            <div className="font-size-7 shade-20 margin-bottom-32 uppercase font-weight-bold shade-10">
              {categoryTitle}
            </div>
            <WebsiteText variant="display" className="margin-bottom-24">
              {job.title}
            </WebsiteText>
            <div className="simple-rich-text font-size-5">
              {introSection && (
                <SanityTextRenderer
                  variant="website"
                  content={introSection.content}
                />
              )}
            </div>
            <div className="padding-top-24">
              <Button href={job.applyUrl} variant="primary" size="huge">
                <FormattedMessage
                  id="jobs.show.apply"
                  defaultMessage="Apply for this position"
                />
              </Button>
            </div>
          </div>

          {/* Job Sections */}
          <div
            className="padding-top-40"
            css={{
              borderTop: '1px solid var(--shade-01)',
            }}
          >
            {otherSections?.map((section, index) => (
              <JobDescriptionSection title={section.title} key={index}>
                <SanityTextRenderer
                  variant="website"
                  content={section.content}
                />
              </JobDescriptionSection>
            ))}
            <JobDescriptionSection title="What we offer">
              <ReactMarkdown>{SECTION_WHAT_WE_OFFER}</ReactMarkdown>
            </JobDescriptionSection>

            {!!teamPeople?.length && (
              <div>
                <h2 className="font-size-3 font-family-title font-weight-medium padding-bottom-32">
                  <FormattedMessage
                    defaultMessage="Who you will work with"
                    id="jobs.show.whoYouWillWorkWith"
                  />
                </h2>

                <div className="grid-12 grid-gap-32">
                  {teamPeople.map((person) => (
                    <div
                      className="grid-span-3 grid-span-4-medium grid-span-6-small"
                      key={person._id}
                    >
                      <ImgSanity
                        src={person.profilePicture.url}
                        alt={person.name}
                        className="margin-bottom-8 radius-large"
                      />

                      <h5 className="font-family-title font-weight-medium font-size-6 margin-bottom-2">
                        {person.name}
                      </h5>
                      <p className="font-size-7 shade-20">{person.position}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>
      <SectionCurve from="moon-dust" to="white" shape="top-large" />
      <SectionWorkCulture background="white" />
      <Section background="white" className="text-align-center">
        <div className="font-size-3 font-family-title font-weight-medium padding-bottom-24">
          <FormattedMessage
            id="jobs.show.readyToApply"
            defaultMessage="Ready to apply?"
          />
        </div>
        <Button href={job.applyUrl} variant="primary" size="huge">
          <FormattedMessage
            id="jobs.show.apply"
            defaultMessage="Apply for this position"
          />
        </Button>
      </Section>
      <SectionCurve from="white" to="moon-dust" shape="bottom-medium" />
    </SlitePage>
  )
}
